import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '@/utils/api';
import type { WeeklyStats, MonthlyDelta, CareerScore, TimelineEvent, Recommendation } from '@/types';

interface AnalyticsState {
  weeklyStats: WeeklyStats | null;
  monthlyDelta: MonthlyDelta | null;
  careerScore: CareerScore | null;
  timeline: TimelineEvent[];
  recommendations: Recommendation[];
  isLoading: boolean;
  error: string | null;
}

const initialState: AnalyticsState = {
  weeklyStats: null,
  monthlyDelta: null,
  careerScore: null,
  timeline: [],
  recommendations: [],
  isLoading: false,
  error: null,
};

export const fetchWeeklyStats = createAsyncThunk(
  'analytics/fetchWeeklyStats',
  async (weekStart: string | undefined, { rejectWithValue }) => {
    try {
      const params = weekStart ? `?weekStart=${weekStart}` : '';
      const response = await api.get(`/analytics/weekly${params}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch weekly stats'
      );
    }
  }
);

export const fetchMonthlyDelta = createAsyncThunk(
  'analytics/fetchMonthlyDelta',
  async (month: string | undefined, { rejectWithValue }) => {
    try {
      const params = month ? `?month=${month}` : '';
      const response = await api.get(`/analytics/monthly${params}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch monthly delta'
      );
    }
  }
);

export const fetchCareerScore = createAsyncThunk(
  'analytics/fetchCareerScore',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/analytics/score');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch career score'
      );
    }
  }
);

export const fetchTimeline = createAsyncThunk(
  'analytics/fetchTimeline',
  async (params: { limit?: number; offset?: number; type?: string } = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.set('limit', params.limit.toString());
      if (params.offset) queryParams.set('offset', params.offset.toString());
      if (params.type) queryParams.set('type', params.type);

      const response = await api.get(`/analytics/timeline?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch timeline'
      );
    }
  }
);

export const fetchRecommendations = createAsyncThunk(
  'analytics/fetchRecommendations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/ml/recommendations');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch recommendations'
      );
    }
  }
);

const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    clearAnalytics: (state) => {
      state.weeklyStats = null;
      state.monthlyDelta = null;
      state.careerScore = null;
      state.timeline = [];
      state.recommendations = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Weekly stats
      .addCase(fetchWeeklyStats.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchWeeklyStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.weeklyStats = action.payload;
      })
      .addCase(fetchWeeklyStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Monthly delta
      .addCase(fetchMonthlyDelta.fulfilled, (state, action) => {
        state.monthlyDelta = action.payload;
      })
      // Career score
      .addCase(fetchCareerScore.fulfilled, (state, action) => {
        state.careerScore = action.payload;
      })
      // Timeline
      .addCase(fetchTimeline.fulfilled, (state, action) => {
        state.timeline = action.payload;
      })
      // Recommendations
      .addCase(fetchRecommendations.fulfilled, (state, action) => {
        state.recommendations = action.payload;
      });
  },
});

export const { clearAnalytics } = analyticsSlice.actions;
export default analyticsSlice.reducer;
